import { t } from "../i18n";
import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { Search, LoaderCircle, RefreshCw } from "lucide-react";
import { api } from "../api";
import { SettingsPage } from "./settings/ProfilePage";
import { serverLogsErrorMessage } from "./ServerLogsPage.utils";

type ServerLogs = {
  source: "docker" | "file" | "victorialogs";
  tail: number;
  content: string;
};

export function ServerLogsPage() {
  const [tail, setTail] = useState(500);
  const [range, setRange] = useState("1h");
  const [level, setLevel] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [live, setLive] = useState(true);
  const [logs, setLogs] = useState<ServerLogs>();
  const [source, setSource] = useState<ServerLogs["source"]>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestRef = useRef<{
    controller: AbortController;
    id: number;
  } | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (quiet = false) => {
      requestRef.current?.controller.abort();
      const requestId = ++requestIdRef.current;
      const controller = new AbortController();
      requestRef.current = { controller, id: requestId };
      if (!quiet) setLoading(true);
      setError("");
      if (!quiet) setLogs(undefined);
      try {
        const nextLogs = await api<ServerLogs>(
          `/admin/logs?${new URLSearchParams({ tail: String(tail), range, level, search })}`,
          {
            signal: controller.signal,
          },
        );
        if (requestRef.current?.id !== requestId) return;
        setLogs(nextLogs);
        setSource(nextLogs.source);
      } catch (value) {
        if (controller.signal.aborted || requestRef.current?.id !== requestId)
          return;
        setLogs(undefined);
        setError(serverLogsErrorMessage(value));
      } finally {
        if (requestRef.current?.id === requestId) {
          requestRef.current = null;
          if (!quiet) setLoading(false);
        }
      }
    },
    [tail, range, level, search],
  );

  useEffect(() => {
    void load();
    const timer = live
      ? window.setInterval(() => {
          if (!document.hidden && !requestRef.current) void load(true);
        }, 5000)
      : undefined;
    return () => {
      window.clearInterval(timer);
      requestRef.current?.controller.abort();
    };
  }, [load, live]);

  return (
    <SettingsPage
      fillHeight
      title={t("Server logs")}
      description="Explore runtime logs, charts, and live queries, or search recent events below."
      action={
        <div className="flex flex-wrap gap-2">
          <Link className="button" to="/settings/logs/explorer">
            <Search size={15} aria-hidden="true" /> Open log explorer
          </Link>
          <select
            aria-label="Maximum log lines"
            className="field w-auto"
            value={tail}
            onChange={(event) => setTail(Number(event.target.value))}
          >
            <option value={200}>{t("Last 200 lines")}</option>
            <option value={500}>{t("Last 500 lines")}</option>
            <option value={1000}>{t("Last 1,000 lines")}</option>
            <option value={2000}>{t("Last 2,000 lines")}</option>
          </select>
          <button
            type="button"
            className="button border border-stone-200 dark:border-white/10"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCw size={15} /> {t("Refresh")}
          </button>
        </div>
      }
    >
      <form
        className="mb-4 flex shrink-0 flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          const value = searchInput.trim();
          if (value === search) void load();
          else setSearch(value);
        }}
      >
        <label className="flex min-w-48 flex-1 flex-col gap-1 text-sm">
          Search logs
          <input
            className="field"
            value={searchInput}
            maxLength={256}
            disabled={source !== undefined && source !== "victorialogs"}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Message, request or generation ID"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Time range
          <select
            className="field w-auto"
            disabled={source !== undefined && source !== "victorialogs"}
            value={range}
            onChange={(event) => setRange(event.target.value)}
          >
            <option value="15m">Last 15 minutes</option>
            <option value="1h">Last hour</option>
            <option value="6h">Last 6 hours</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Severity
          <select
            className="field w-auto"
            disabled={source !== undefined && source !== "victorialogs"}
            value={level}
            onChange={(event) => setLevel(event.target.value)}
          >
            <option value="">All levels</option>
            <option value="error">Error</option>
            <option value="warn">Warning</option>
            <option value="info">Info</option>
            <option value="debug">Debug</option>
          </select>
        </label>
        <button
          className="button"
          type="submit"
          disabled={
            loading || (source !== undefined && source !== "victorialogs")
          }
        >
          Search
        </button>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={live}
            onChange={(event) => setLive(event.target.checked)}
          />
          Auto-refresh
        </label>
      </form>
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm whitespace-pre-line text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}
      <section
        aria-busy={loading}
        className="panel min-h-48 min-w-0 overflow-hidden bg-stone-950 text-stone-200 md:min-h-0 md:flex-1"
      >
        {loading && !logs ? (
          <div className="grid h-full min-h-48 place-items-center text-stone-500 md:min-h-0">
            <LoaderCircle className="animate-spin" />
          </div>
        ) : (
          <pre
            tabIndex={0}
            aria-label="Runtime log output"
            className="m-0 p-4 font-mono text-xs leading-5 break-words whitespace-pre-wrap md:h-full md:overflow-auto md:overscroll-contain"
          >
            {logs?.content || "No log output."}
          </pre>
        )}
      </section>
      <p className="mt-3 shrink-0 text-xs text-stone-400">
        {t(
          "Source: {{source}}. Auto-refresh checks every 5 seconds while this page is visible.",
          {
            source:
              source === "victorialogs"
                ? "VictoriaLogs"
                : source === "docker"
                  ? "Docker container logs"
                  : "local API log file",
          },
        )}
      </p>
    </SettingsPage>
  );
}
